import { useState, useEffect, useCallback, type ReactNode } from 'react';
import {
  type User,
  login as apiLogin,
  logout as apiLogout,
  getCurrentUser,
  getToken,
  setToken,
  setStoredUser,
  getStoredUser,
} from '../services/auth.api';
import { AuthContext } from './useAuth';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const refreshUser = useCallback(async () => {
    const current = await getCurrentUser();
    if (current) {
      setUser(current);
      setStoredUser(current);
    }
    // null = any failure (network, 5xx), not just a dead session: keep the current user.
  }, []);

  // Any 403 means our permissions may be stale (role changed while the tab was open).
  useEffect(() => {
    const onForbidden = () => { void refreshUser(); };
    window.addEventListener('fleet:forbidden', onForbidden);
    return () => window.removeEventListener('fleet:forbidden', onForbidden);
  }, [refreshUser]);

  // A promoted user with no write buttons never hits a 403 — catch up when the tab regains focus.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible' && getToken()) void refreshUser();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [refreshUser]);

  const can = useCallback(
    (permission: string) => !user?.permissions || user.permissions.includes(permission),
    [user]
  );

  // Check for existing session on mount
  useEffect(() => {
    const initAuth = async () => {
      const token = getToken();
      if (token) {
        // Try to get user from localStorage first for faster load
        const storedUser = getStoredUser();
        if (storedUser) {
          setUser(storedUser);
        }
        
        // Verify token is still valid
        const currentUser = await getCurrentUser();
        if (currentUser) {
          setUser(currentUser);
          setStoredUser(currentUser);
        } else {
          // Token is invalid, clear everything
          apiLogout();
          setUser(null);
        }
      }
      setIsLoading(false);
    };

    initAuth();
  }, []);

  const login = async (username: string, password: string) => {
    try {
      const response = await apiLogin(username, password);
      
      if (response.success && response.token && response.user) {
        setToken(response.token);
        setStoredUser(response.user);
        setUser(response.user);
        return { success: true };
      }
      
      return { success: false, error: response.error || 'Login failed' };
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: 'Login failed. Please try again.' };
    }
  };

  const logout = () => {
    apiLogout();
    setUser(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        logout,
        can,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
